import { useGetAllQuestionsV2ApplyQuery } from '@core/app/api/applyJobV2Api'
import {
    ApplyV2CustomFieldTypeConst,
    type ApplyV2Question,
    type ApplyV2StarRatingQuestion,
    type GetAllQuestionsV2Response,
} from '@core/app/types/applyV2Type'
import ApplyV2AttachmentsUploader from '@core/components/ApplyDialogV2/ApplyV2AttachmentsUploader'
import ApplyV2DataRow from '@core/components/ApplyDialogV2/ApplyV2DataRow'
import ApplyV2Picture from '@core/components/ApplyDialogV2/ApplyV2Picture'
import FileUploaderV2List from '@core/components/ApplyDialogV2/FileUploaderV2List'
import { useApplyV2Context } from '@core/utils/contexts/ApplyV2ModalContext/ApplyV2ModalContext'
import ApplySkeleton from '@core/components/ApplyDialogV2/ApplySkeleton'
import decodeHtmlEntitiesRecursive from '@core/utils/helpers/decodeHtmlEntitiesRecursive'
import getCurrencyById from '@core/utils/helpers/getCurrencyById'
import useApplyV2NavTranslations from '@core/utils/hooks/useApplyV2NavTranslations'
import useFormattedDatesPeriod from '@core/utils/hooks/useFormattedDatesPeriod'
import React, { useEffect } from 'react'
import './ApplyV2ReviewApplyQuestions.scss'

function sortQuestionsRecursively(questions: ApplyV2Question[]): ApplyV2Question[] {
    return [...questions]
        .sort((a, b) => a.orderId - b.orderId)
        .map((question) => ({
            ...question,
            children: question.children ? sortQuestionsRecursively(question.children) : [],
        }))
        .filter((q) => q.visible)
}

const baseClass = 'ApplyV2ReviewApplyQuestions'

interface Props {
    className?: string
    isShowExpandButton?: boolean
    questionsData?: GetAllQuestionsV2Response | undefined | null
    isQuestionsLoading?: boolean
}

function ApplyV2ReviewApplyQuestions({ questionsData, isQuestionsLoading }: Props) {
    const { storageData } = useApplyV2Context()
    //
    // const { data: questionsData, isLoading: isQuestionsLoading, isFetching, refetch } = useGetAllQuestionsV2ApplyQuery(
    //     { uid: storageData?.postInitUid || '' },
    //     { skip: !storageData?.postInitUid }
    // )

    const questions = questionsData?.data?.questions ? sortQuestionsRecursively([...questionsData.data.questions]) : []

    // console.log(questions)

    if (isQuestionsLoading) {
        return (
            <div className={baseClass}>
                <ApplySkeleton isLoading={isQuestionsLoading} />
                <ApplySkeleton isLoading={isQuestionsLoading} />
                <ApplySkeleton isLoading={isQuestionsLoading} />
            </div>
        )
    }
    return (
        <div className={baseClass}>
            <ReviewQuestionsList list={questions} />
        </div>
    )
}

function ReviewQuestionsList({ list }: { list: ApplyV2Question[] | null | undefined }) {
    // const questions = (list ? [...list] : []).sort((a, b) => a.orderId - b.orderId)

    const questions = list ? sortQuestionsRecursively([...list]) : []

    if (questions.length === 0) {
        return null
    }

    return (
        <div className={`${baseClass}__list`}>
            {questions.map((question, index) => {
                return <ReviewQuestionItem key={question.questionId} question={question} />
            })}
        </div>
    )
}

function ReviewQuestionItem({ question }: { question: ApplyV2Question }) {
    const { getTitle } = useApplyV2NavTranslations()
    const { formatDateRange } = useFormattedDatesPeriod()

    const renderAnswer = () => {
        const hasValue = (value: any): boolean => {
            if (value == null) return false
            if (typeof value === 'string') return value.trim() !== ''
            if (typeof value === 'number') return true
            if (typeof value === 'boolean') return true
            if (Array.isArray(value)) return value.length > 0
            if (typeof value === 'object') return Object.keys(value).length > 0
            return false
        }

        switch (question.type) {
            case 'starRating':
                return (
                    question.visible &&
                    question.answer?.value &&
                    question.answer?.value > 0 && (
                        <ApplyV2DataRow title={decodeHtmlEntitiesRecursive(question.name)}>
                            <span className={`${baseClass}__rating`}>
                                <span className={`${baseClass}__rating__star`}>
                                    <svg
                                        width="20"
                                        height="20"
                                        viewBox="0 0 20 20"
                                        fill="none"
                                        xmlns="http://www.w3.org/2000/svg"
                                    >
                                        <path
                                            fillRule="evenodd"
                                            clipRule="evenodd"
                                            d="M11.05 1.8176C10.6199 0.949471 9.3801 0.949471 8.95003 1.8176L6.77815 6.20209L1.92047 6.90532C0.962761 7.04398 0.572374 8.22241 1.27187 8.90174L4.78527 12.3138L3.95598 17.1309C3.79046 18.0924 4.80071 18.8123 5.65465 18.365L10 16.0889L14.3455 18.365C15.1995 18.8125 16.2094 18.0921 16.044 17.1309L15.2147 12.3138L18.728 8.9018C19.4278 8.22236 19.037 7.04397 18.0795 6.90532L13.2219 6.20208L11.05 1.8176Z"
                                            fill="#FFBE33"
                                        />
                                    </svg>
                                </span>
                                {question.answer?.value || ''}
                            </span>
                        </ApplyV2DataRow>
                    )
                )

            case 'attachment':
                return (
                    // (question?.answer?.value ?? []).length > 0 && (
                    (question?.answer?.value ?? []).length > 0 &&
                    question.visible && (
                        <ApplyV2DataRow title={decodeHtmlEntitiesRecursive(question.name)}>
                            <ApplyV2AttachmentsUploader isShowInput={false} files={question?.answer?.value ?? []} />
                        </ApplyV2DataRow>
                    )
                )

            case 'text':
            case 'longText':
            case 'number':
                return (
                    question.answer?.value &&
                    question.visible && (
                        <ApplyV2DataRow title={decodeHtmlEntitiesRecursive(question.name)}>
                            {question.answer?.value}
                        </ApplyV2DataRow>
                    )
                )

            case 'yesNo':
            case 'qualifying':
                const selectedValue = (question.options ?? []).find((opt) => opt.id === question.answer?.value)
                return (
                    selectedValue &&
                    question.visible && (
                        <ApplyV2DataRow title={decodeHtmlEntitiesRecursive(question.name)}>
                            {selectedValue.name.toLowerCase() === 'yes'
                                ? getTitle('128', 'Yes')
                                : getTitle('129', 'No')}
                        </ApplyV2DataRow>
                    )
                )

            case 'date':
                return (
                    question.answer?.value &&
                    question.visible && (
                        <ApplyV2DataRow title={decodeHtmlEntitiesRecursive(question.name)}>
                            {formatDateRange(question.answer?.value, null, {
                                isShowPresent: false,
                                dateFormat: 'D MMM YYYY',
                            })}
                        </ApplyV2DataRow>
                    )
                )

            case 'dateRange':
                return (
                    (question.answer?.value?.[0] || question.answer?.value?.[1]) &&
                    question.visible && (
                        <ApplyV2DataRow title={decodeHtmlEntitiesRecursive(question.name)}>
                            {formatDateRange(question.answer?.value?.[0] ?? null, question.answer?.value?.[1] ?? null, {
                                isShowPresent: true,
                                dateFormat: 'D MMM YYYY',
                            })}
                        </ApplyV2DataRow>
                    )
                )

            case 'singleSelect':
                return (
                    // question.answer && (
                    question.answer?.value &&
                    question.visible && (
                        <ApplyV2DataRow title={decodeHtmlEntitiesRecursive(question.name)}>
                            {(question.options ?? []).find(({ id }) => id === question.answer?.value)?.name}
                        </ApplyV2DataRow>
                    )
                )
            case 'multiSelect':
                const selectedValues = (question.answer?.value ?? []) as number[]
                const options = question.options ?? []

                const selectedOptions = selectedValues.map((id) => {
                    return options.find((option) => option.id === id)?.name ?? ''
                })
                return (
                    selectedValues.length > 0 &&
                    question.visible && (
                        <ApplyV2DataRow title={decodeHtmlEntitiesRecursive(question.name)}>
                            {selectedOptions.join(', ')}
                        </ApplyV2DataRow>
                    )
                )
            case 'customField':
                switch (question.customField.typeId) {
                    case ApplyV2CustomFieldTypeConst.SingleSelect:
                        return (
                            question.answer &&
                            question.visible && (
                                <ApplyV2DataRow title={decodeHtmlEntitiesRecursive(question.name)}>
                                    {(question.options ?? []).find(({ id }) => id === question.answer?.value)?.name}
                                </ApplyV2DataRow>
                            )
                        )

                    case ApplyV2CustomFieldTypeConst.MultiSelect: {
                        const selectedValues = (question.answer?.value ?? []) as number[]
                        const options = question.options ?? []
                        const selectedOptions = selectedValues.map(
                            (id) => options.find((option) => option.id === id)?.name ?? ''
                        )
                        return (
                            selectedValues.length > 0 &&
                            question.visible && (
                                <ApplyV2DataRow title={decodeHtmlEntitiesRecursive(question.name)}>
                                    {selectedOptions.join(', ')}
                                </ApplyV2DataRow>
                            )
                        )
                    }

                    case ApplyV2CustomFieldTypeConst.Text:
                        return (
                            question.answer?.value &&
                            question.visible && (
                                <ApplyV2DataRow title={decodeHtmlEntitiesRecursive(question.name)}>
                                    {question.answer?.value}
                                </ApplyV2DataRow>
                            )
                        )

                    case ApplyV2CustomFieldTypeConst.Integer: {
                        const value = question.answer?.value
                        const decimals = question.customField.properties?.decimals ?? 0

                        if (typeof value === 'number') {
                            const flag = getCurrencyById(question.customField.properties?.currencyId)
                            if (flag && value) {
                                return (
                                    question.visible && (
                                        <ApplyV2DataRow title={decodeHtmlEntitiesRecursive(question.name)}>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                                <ApplyV2Picture
                                                    className={`${baseClass}__currency`}
                                                    fallbackSrc={`${window.location.protocol}//${window.location.hostname}/${flag?.img}`}
                                                    alt=""
                                                />
                                                <span>{value.toFixed(decimals)}</span>
                                            </div>
                                        </ApplyV2DataRow>
                                    )
                                )
                            }
                            return (
                                question.visible &&
                                !!value && (
                                    <ApplyV2DataRow title={decodeHtmlEntitiesRecursive(question.name)}>
                                        {value.toFixed(decimals)}
                                    </ApplyV2DataRow>
                                )
                            )
                        }
                        return (
                            !!value &&
                            question.visible && (
                                <ApplyV2DataRow title={decodeHtmlEntitiesRecursive(question.name)}>
                                    {String(value)}
                                </ApplyV2DataRow>
                            )
                        )
                    }

                    case ApplyV2CustomFieldTypeConst.Date: {
                        const answerValue = question.answer?.value
                        const [from, to] = Array.isArray(answerValue)
                            ? (answerValue as (string | number | null | undefined)[])
                            : [answerValue]
                        return (
                            (from ?? to) &&
                            question.visible && (
                                <ApplyV2DataRow title={decodeHtmlEntitiesRecursive(question.name)}>
                                    {formatDateRange(from, to, {
                                        isShowPresent: false,
                                        dateFormat: 'D MMMM YYYY',
                                    })}
                                </ApplyV2DataRow>
                            )
                        )
                    }

                    default:
                        return
                }

            case 'attribute':
                if (!question.answer || !question.visible) {
                    return null
                }
                return (
                    <ApplyV2DataRow title={decodeHtmlEntitiesRecursive(question.name)}>
                        <div className={`${baseClass}__list`}>
                            {question.attribute.bool && (
                                <div>
                                    {question.answer?.value?.bool ? getTitle('128', 'Yes') : getTitle('129', 'No')}
                                </div>
                            )}
                            {question.attribute.checkbox && (
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                                    {Object.values(question.answer?.value?.checkbox ?? []).map(({ id, rating }) => {
                                        const selected = (question.attribute?.checkboxes ?? []).find(
                                            (option) => option.id === id
                                        )?.name
                                        return (
                                            <div
                                                key={id}
                                                style={{ display: 'flex', alignItems: 'center', gap: '12px' }}
                                            >
                                                <span>{selected}</span>
                                                {rating && rating > 0 ? (
                                                    <span className={`${baseClass}__rating`}>
                                                        <span className={`${baseClass}__rating__star`}>
                                                            <svg
                                                                width="20"
                                                                height="20"
                                                                viewBox="0 0 20 20"
                                                                fill="none"
                                                                xmlns="http://www.w3.org/2000/svg"
                                                            >
                                                                <path
                                                                    fillRule="evenodd"
                                                                    clipRule="evenodd"
                                                                    d="M11.05 1.8176C10.6199 0.949471 9.3801 0.949471 8.95003 1.8176L6.77815 6.20209L1.92047 6.90532C0.962761 7.04398 0.572374 8.22241 1.27187 8.90174L4.78527 12.3138L3.95598 17.1309C3.79046 18.0924 4.80071 18.8123 5.65465 18.365L10 16.0889L14.3455 18.365C15.1995 18.8125 16.2094 18.0921 16.044 17.1309L15.2147 12.3138L18.728 8.9018C19.4278 8.22236 19.037 7.04397 18.0795 6.90532L13.2219 6.20208L11.05 1.8176Z"
                                                                    fill="#FFBE33"
                                                                />
                                                            </svg>
                                                        </span>
                                                        <span>{rating}</span>
                                                    </span>
                                                ) : (
                                                    <span></span>
                                                )}
                                            </div>
                                        )
                                    })}
                                </div>
                            )}
                            {question.attribute.date && question.answer?.value.date && (
                                <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                    {formatDateRange(question.answer?.value.date, null, {
                                        isShowPresent: false,
                                        dateFormat: 'D MMMM YYYY',
                                    })}
                                </div>
                            )}
                            {question.attribute.dropdown && question.answer?.value.dropdown && (
                                <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                    {(() => {
                                        return (question.attribute.dropdowns ?? []).find(
                                            ({ id }) => id === question.answer?.value.dropdown
                                        )?.name
                                    })()}
                                </div>
                            )}
                            {question.attribute.rating && question.answer?.value.rating && (
                                <span className={`${baseClass}__rating`}>
                                    <span className={`${baseClass}__rating__star`}>★</span>
                                    {question.answer?.value?.rating}
                                </span>
                            )}
                            {question.attribute.shortText && question.answer?.value.shortText && (
                                <span>{question.answer?.value.shortText}</span>
                            )}
                            {question.attribute.text && question.answer?.value.text && (
                                <span>{question.answer?.value.text}</span>
                            )}
                        </div>
                    </ApplyV2DataRow>
                )

            default:
                return null
        }
    }

    return (
        <div className={`${baseClass}__item`}>
            {/*<ApplyV2DataRow title={question.name}>{renderAnswer()}</ApplyV2DataRow>*/}
            {renderAnswer()}
            {question.children && <ReviewQuestionsList list={question.children} />}
        </div>
    )
}

export default ApplyV2ReviewApplyQuestions
